import Multiselect from 'vue-multiselect';
import p from 'vue-strict-prop';
import * as tsx from 'vue-tsx-support';
import { Settings } from '@codegrade/icons';
import { Maybe } from '@codegrade/maybe';
import { errors } from '@codegrade/utils';
import { makeInputValue } from '@/components/Editors';
import * as Editors from '@/components/Editors';
import * as Form from '@/components/Form';
import { Icon } from '@/components/Icon';
import LoaderComp from '@/components/Loader';
import PromiseLoader from '@/components/PromiseLoader';
import * as models from '@/models';
import { PreferenceStore } from '@/store';
import * as utils from '@/utils';
import { SHOW_INLINE_FEEDBACKS } from '@/utils/props';
const MIN_LOAD_TIME = 200;
const ThemeSelect = Form.createTypedSelect();
const TabPresetSelect = Form.createTypedSelect();
const ShowInlineFeedbackSelect = Form.createTypedSelect();
export default tsx.componentFactoryOf().create({
    name: 'preference-manager',
    props: {
        fileId: p(String).optional,
        showWhitespace: p(Boolean).default(true),
        showWhitespacePreset: p(Boolean).default(false),
        showLanguage: p(Boolean).default(true),
        showFontSize: p(Boolean).default(true),
        showInlineFeedback: p(Boolean).default(true),
        showTheme: p(Boolean).default(true),
        showContextAmount: p(Boolean).default(false),
        showTabPreset: p(Boolean).default(false),
        inPopover: p(Boolean).default(false),
        boundary: p(String).default('window'),
        container: p(String).optional,
        placement: p(String).default('bottom'),
    },
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    data() {
        const uniqueId = utils.getUniqueId();
        return {
            uniqueId,
            loading: true,
            themePromise: null,
            contextAmountPromise: null,
            fontSizePromise: null,
            tabPresetPromise: null,
            languagePromise: null,
            whitespacePromise: null,
            whitespacePresetPromise: null,
            selectedLanguage: 'Default',
            popoverDisabled: false,
            languages: Maybe.empty(),
            dropdownShowing: 0,
        };
    },
    computed: {
        /** @returns The id of the preference manager button that opens the settings modal/popover. */
        btnId() {
            return `settings-toggle-${this.uniqueId}`;
        },
        /** @returns The id of the preference manager popover. */
        popoverId() {
            return `settings-popover-${this.uniqueId}`;
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        fontSize() {
            return PreferenceStore.fontSize();
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        contextAmount() {
            return PreferenceStore.contextAmount();
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        theme() {
            return models.Theme.get();
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        tabPreset() {
            return models.SubmissionTabPreset.get();
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        whitespace() {
            if (this.fileId == null || this.fileId === '') {
                return this.whitespacePreset;
            }
            return models.WhitespaceForFile.get(this.fileId);
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        whitespacePreset() {
            return models.WhitespacePreset.get();
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        inlineFeedback() {
            switch (this.$route.query.showInlineFeedback) {
                case 'none':
                case 'false':
                    return 'none';
                case 'only-normal':
                    return 'only-normal';
                case 'only-peer':
                    return 'only-peer';
                case 'all':
                default:
                    return 'all';
            }
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        settingsContentNode() {
            return utils.ifExpr(this.loading, () => <LoaderComp scale={1}/>, () => (<div ref="settings-node" data-cy="preferences" class="form-layout-stacked" tabindex={0}>
                        {this.whitespaceNode}
                        {this.whitespacePresetNode}
                        {this.languageNode}
                        {this.fontSizeNode}
                        {this.inlineFeedbackNode}
                        {this.contextAmountNode}
                        {this.themeNode}
                        {this.tabPresetNode}
                    </div>));
        },
        /** @returns The boolean toggle for whether whitespaces should be shown in the current code viewer. */
        whitespaceNode() {
            return utils.ifOrEmpty(this.showWhitespace, () => (<Form.Field>
                    <Form.Label>
                        Show Whitespace (current file)
                        {this.renderLoader(this.whitespacePromise)}
                    </Form.Label>
                    <Form.Toggle data-cy="show-whitespace-file-toggle" checked={this.whitespace} onInput={newValue => this.whitespaceChanged(newValue)}/>
                    <Form.Description>
                        Toggle whether whitespace is shown or hidden for this file.
                    </Form.Description>
                </Form.Field>));
        },
        /** @returns The boolean toggle for whether whitespaces should be shown by default. */
        whitespacePresetNode() {
            return utils.ifOrEmpty(this.showWhitespacePreset, () => (<Form.Field>
                    <Form.Label>
                        Show Whitespace (default)
                        {this.renderLoader(this.whitespacePresetPromise)}
                    </Form.Label>
                    <Form.Toggle data-cy="show-whitespace-preset-toggle" checked={this.whitespacePreset} onInput={newValue => this.whitespacePresetChanged(newValue)}/>
                    <Form.Description>
                        Sets the default whitespace visibility for code viewers.
                    </Form.Description>
                </Form.Field>));
        },
        /** @returns The input for the cede editor font size. */
        fontSizeNode() {
            return utils.ifOrEmpty(this.showFontSize, () => (<Form.Field scopedSlots={{
                    default: ({ inputId, descriptionId }) => [
                        <Form.Label>
                                Code font size
                                {this.renderLoader(this.fontSizePromise)}
                            </Form.Label>,
                        <Editors.Number id={inputId} described-by={descriptionId} name="Code font size" value={makeInputValue(this.fontSize)} onInput={val => this.fontSizeChanged(val)} class="cg-form-control grow" step={1} min={1}/>,
                        <Form.Description>
                                Set the font size (in pixels) for code blocks.
                            </Form.Description>,
                    ],
                }}/>));
        },
        /** @returns The dropdown for selecting the site theme. */
        themeNode() {
            return utils.ifOrEmpty(this.showTheme, () => (<Form.Field>
                    <Form.Label>Theme</Form.Label>
                    <ThemeSelect data-cy="theme-select" options={['light', 'dark', 'auto'].map(value => ({
                    value,
                    label: this.themeText(value),
                }))} value={this.theme} onInput={newVal => this.themeChanged(newVal)}/>
                    <Form.Description>
                        Select theme preference: Light, Dark, or follow the System settings.
                    </Form.Description>
                </Form.Field>));
        },
        /** @returns The dropdown for selecting the language. */
        languageNode() {
            return utils.ifOrEmpty(this.showLanguage, () => {
                this.loadLanguages();
                return (<Form.Field scopedSlots={{
                        default: ({ inputId, descriptionId }) => [
                            <Form.Label>
                                    Programming language
                                    {this.renderLoader(this.languagePromise)}
                                </Form.Label>,
                            this.languages.caseOf({
                                Nothing: () => <LoaderComp scale={1}/>,
                                Just: languages => (<Multiselect id={inputId} described-by={descriptionId} 
                                // @ts-expect-error: This component doesn't declare its props.
                                value={this.selectedLanguage} onInput={(lang) => this.languageChanged(lang)} hideSelected={this.selectedLanguage === 'Default'} deselectLabel="Reset language" selectLabel="Select language" options={languages} onOpen={() => this.disablePopover()} onClose={() => this.enablePopover()} tabindex={0}/>),
                            }),
                            <Form.Description>
                                    Select the code language for syntax highlighting.
                                </Form.Description>,
                        ],
                    }}/>);
            });
        },
        /** @returns The dropdown for the inline feedback. */
        inlineFeedbackNode() {
            return utils.ifOrEmpty(this.showInlineFeedback, () => (<Form.Field>
                    <Form.Label>
                        Inline Feedback
                        {this.renderLoader(this.languagePromise)}
                    </Form.Label>
                    <ShowInlineFeedbackSelect options={SHOW_INLINE_FEEDBACKS.map(value => ({
                    value,
                    label: this.inlineFeedbackText(value),
                }))} value={this.inlineFeedback} onInput={newValue => this.emitInlineFeedback(newValue, true)}/>
                    <Form.Description>
                        Choose how inline feedback comments will be displayed.
                    </Form.Description>
                </Form.Field>));
        },
        /** @returns The number input for the amount of context lines to be shown for inline feedback messages. */
        contextAmountNode() {
            return utils.ifOrEmpty(this.showContextAmount, () => (<Form.Field scopedSlots={{
                    default: ({ inputId }) => [
                        <Form.Label for={inputId}>
                                Amount of context
                                {this.renderLoader(this.contextAmountPromise)}
                            </Form.Label>,
                        <Editors.Number id={inputId} name="Amount of context" value={makeInputValue(this.contextAmount)} onInput={val => this.contextAmountChanged(val)} class="cg-form-control" step={1} min={0}/>,
                        <Form.Description>
                                The number of lines of context shown around inline feedback.
                            </Form.Description>,
                    ],
                }}/>));
        },
        /** @returns The dropdown input for the tab preset. */
        tabPresetNode() {
            return utils.ifOrEmpty(this.showTabPreset, () => (<Form.Field>
                    <Form.Label>
                        Submission tab selection
                        {this.renderLoader(this.tabPresetPromise)}
                    </Form.Label>
                    <TabPresetSelect options={[
                    'smart',
                    'code',
                    'feedback-overview',
                    'auto-test',
                    'peer-feedback',
                ].map(value => ({ label: this.tabPresetText(value), value }))} value={this.tabPreset} onInput={newValue => this.tabPresetChanged(newValue)}/>
                    <Form.Description>
                        Choose the default submission tab to open (Smart, Code, Feedback, AutoTest,
                        or Peer Feedback).
                    </Form.Description>
                </Form.Field>));
        },
    },
    methods: {
        /** @returns The promise loader Node. */
        renderLoader(loader) {
            return (<span class="ml-3!">
                    <PromiseLoader promise={loader} waitAtLeast={MIN_LOAD_TIME} popoverPlacement="top" onError={() => this.disablePopover()} onAfterError={() => this.enablePopover()}/>
                </span>);
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        themeChanged(val) {
            this.themePromise = models.Theme.set(val).then(response => {
                tsx.emitOn(this, 'onTheme', val);
                return response.addCallbacks({
                    onAfterSuccess: () => {
                        this.themePromise = null;
                    },
                });
            });
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        tabPresetChanged(val) {
            this.tabPresetPromise = models.SubmissionTabPreset.set(val).then(response => {
                tsx.emitOn(this, 'onTabPreset', val);
                return response.addCallbacks({
                    onAfterSuccess: () => {
                        this.tabPresetPromise = null;
                    },
                });
            });
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        contextAmountChanged(val) {
            val.caseOf({
                Left: err => {
                    this.contextAmountPromise = Promise.reject(err);
                },
                Right: maybeValue => maybeValue.ifJust(contextAmount => {
                    PreferenceStore.setContextAmount(contextAmount);
                    tsx.emitOn(this, 'onContextAmount', contextAmount);
                    this.contextAmountPromise = Promise.resolve({
                        onAfterSuccess: () => {
                            this.contextAmountPromise = null;
                        },
                    });
                }),
            });
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        fontSizeChanged(val) {
            val.caseOf({
                Left: err => {
                    this.fontSizePromise = Promise.reject(err);
                },
                Right: maybeValue => maybeValue.ifJust(fontSize => {
                    PreferenceStore.setFontSize(fontSize);
                    tsx.emitOn(this, 'onFontSize', fontSize);
                    this.fontSizePromise = Promise.resolve({
                        onAfterSuccess: () => {
                            this.contextAmountPromise = null;
                        },
                    });
                }),
            });
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        whitespaceChanged(val) {
            if (this.fileId == null) {
                return;
            }
            this.whitespacePromise = models.WhitespaceForFile.set(this.fileId, val).then(response => response.addCallbacks({
                onAfterSuccess: () => {
                    this.whitespacePromise = null;
                },
            }));
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        whitespacePresetChanged(val) {
            this.whitespacePresetPromise = models.WhitespacePreset.set(val).then(response => {
                tsx.emitOn(this, 'onWhitespacePreset', val);
                return response.addCallbacks({
                    onAfterSuccess: () => {
                        this.whitespacePresetPromise = null;
                    },
                });
            });
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        languageChanged(lang) {
            if (this.fileId == null) {
                return;
            }
            if (lang == null) {
                this.selectedLanguage = 'Default';
            }
            else {
                this.selectedLanguage = lang;
            }
            this.languagePromise = this.$hlanguageStore
                .setItem(this.fileId, this.selectedLanguage)
                .then(() => ({
                onAfterSuccess: () => {
                    this.languagePromise = null;
                },
            }));
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        async loadValues(fileId) {
            this.loading = true;
            this.emitInlineFeedback(this.inlineFeedback, false);
            await Promise.all([
                this.loadWhitespace(fileId),
                this.loadLanguage(fileId),
                this.loadUserPreferences(),
            ]);
            this.loading = false;
            this.focusPopover();
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        async loadWhitespace(fileId) {
            if (!this.showWhitespace || fileId == null) {
                return;
            }
            try {
                if (this.fileId === '') {
                    await models.WhitespacePreset.load();
                }
                else {
                    await models.WhitespaceForFile.load(fileId);
                }
                this.emitWhitespace();
            }
            catch (caught) {
                const err = errors.coerceToError(caught);
                this.whitespacePromise = Promise.reject(err);
            }
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        emitWhitespace() {
            if (this.fileId != null) {
                tsx.emitOn(this, 'onWhitespace', this.whitespace);
            }
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        async loadLanguage(fileId) {
            if (this.showLanguage === true && fileId != null) {
                await this.$hlanguageStore.getItem(fileId).then(lang => {
                    if (fileId === this.fileId) {
                        this.selectedLanguage = lang ?? 'Default';
                    }
                }, err => {
                    this.languagePromise = Promise.reject(err);
                });
            }
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        async loadUserPreferences() {
            const loaders = [];
            if (this.showTheme) {
                loaders.push(models.Theme.load().catch(err => {
                    this.themePromise = Promise.reject(err);
                    return {};
                }));
            }
            if (this.showTabPreset) {
                loaders.push(models.SubmissionTabPreset.load().catch(err => {
                    this.tabPresetPromise = Promise.reject(err);
                    return {};
                }));
            }
            if (this.showWhitespacePreset || this.showWhitespace) {
                loaders.push(models.WhitespacePreset.load().catch(err => {
                    this.whitespacePresetPromise = Promise.reject(err);
                    return {};
                }));
            }
            await Promise.all(loaders);
        },
        // Disable the popover so a blur event on an element in it will not
        // cause it to close.
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        disablePopover() {
            this.popoverDisabled = true;
        },
        // Enable the popover again. Make sure this happens _after_ the blur
        // event on elements inside it. We focus the button before enabling
        // the popover again to make sure that at least something is focused
        // that, when blurred, will cause the popover to close.
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        async enablePopover() {
            // @ts-expect-error cannot prove that this.$el is an HTMLElement
            // with a focus method.
            this.$el.focus();
            await this.$afterRerender();
            this.popoverDisabled = false;
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        emitInlineFeedback(show, freshChange) {
            tsx.emitOn(this, 'onInlineFeedback', { show, freshChange });
            if (this.inlineFeedback !== show) {
                const query = { ...this.$route.query };
                if (show === 'all') {
                    delete query.showInlineFeedback;
                }
                else {
                    query.showInlineFeedback = show;
                }
                this.$router.replace({ ...this.$route, query });
            }
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        inlineFeedbackText(val) {
            switch (val) {
                case 'all':
                    return 'Show All';
                case 'only-normal':
                    return 'Only Normal';
                case 'only-peer':
                    return 'Only Peer';
                case 'none':
                    return 'Hide All';
                default:
                    throw utils.AssertionError.assertNever(val);
            }
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        themeText(val) {
            switch (val) {
                case 'light':
                    return 'Light';
                case 'dark':
                    return 'Dark';
                case 'auto':
                    return 'System';
                default:
                    throw utils.AssertionError.assertNever(val);
            }
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        tabPresetText(val) {
            switch (val) {
                case 'smart':
                    return 'Smart selection';
                case 'code':
                    return 'Code tab';
                case 'feedback-overview':
                    return 'Feedback Overview tab';
                case 'auto-test':
                    return 'AutoTest tab';
                case 'peer-feedback':
                    return 'Peer Feedback tab';
                default:
                    throw utils.AssertionError.assertNever(val);
            }
        },
        /** Load all available languages if they are not yet loaded. */
        loadLanguages() {
            this.languages.ifNothing(async () => {
                const { listLanguages } = await import('@/utils/highlight');
                const langs = listLanguages();
                langs.push('plain');
                langs.sort(utils.cmpNoCase);
                langs.unshift('Default');
                this.languages = Maybe.of(langs);
                return langs;
            });
        },
        /** Focuses on the popover if it is visible. */
        async focusPopover() {
            await this.$afterRerender();
            await this.$withRef('settings-node', el => {
                utils.focusOn(el);
            });
        },
    },
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    mounted() {
        this.loadValues(this.fileId);
    },
    watch: {
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        whitespace() {
            this.emitWhitespace();
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        selectedLanguage() {
            tsx.emitOn(this, 'onLanguage', this.selectedLanguage);
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        async showWhitespace() {
            await this.loadWhitespace(this.fileId);
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        showLanguage() {
            this.loadLanguage(this.fileId);
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        fileId(newVal, oldVal) {
            if (newVal != null && newVal !== oldVal) {
                this.loadValues(newVal);
            }
        },
        /**
         * Triggered when the dropdown showing changes.
         *
         * When no dropdowns are showing anymore we should focus the popover node again to make it
         * possible to hide it easily.
         */
        dropdownShowing() {
            if (this.dropdownShowing === 0) {
                this.$withRef('settings-node', el => {
                    el.focus?.();
                });
            }
        },
    },
    /** @returns The rendered preference manager component. */
    render() {
        return utils.ifExpr(this.inPopover === true, () => (<b-btn id={this.btnId} class="preference-manager settings-toggle" v-b-popover_hover_focus_top="Settings" aria-label="Submission settings" onClick={() => {
                this.focusPopover();
            }}>
                    <Icon icon={Settings}/>
                    <b-popover triggers={this.dropdownShowing === 0 ? 'click blur' : 'click'} class="preference-manager-panel mb-0 text-left @container" custom-class="overflow-visible" id={this.popoverId} target={this.btnId} placement={this.placement} boundary={this.boundary} container={this.container} disabled={this.popoverDisabled} onShow={() => this.$root.$emit('bv::hide::popover')}>
                        {this.settingsContentNode}
                    </b-popover>
                </b-btn>), () => (<div class="preference-manager-panel pb-2 @container">
                    {this.settingsContentNode}
                </div>));
    },
});
